import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const BusinessCtx = createContext(null);
const LS_KEY = "router_active_business";

export function BusinessProvider({ children }) {
  const { user } = useAuth();
  const [businesses, setBusinesses] = useState([]);
  const [activeId, setActiveId] = useState(localStorage.getItem(LS_KEY) || null);
  const [loading, setLoading] = useState(true);
  const loadedOnce = useRef(false);

  const load = useCallback(async () => {
    if (!user) {
      setBusinesses([]);
      setLoading(false);
      return [];
    }
    if (!loadedOnce.current) setLoading(true);
    try {
      const { data } = await api.get("/businesses");
      setBusinesses(data);
      if (data.length) {
        const stored = localStorage.getItem(LS_KEY);
        const valid = data.find((b) => b.id === stored);
        const pick = valid ? valid.id : data[0].id;
        setActiveId(pick);
        localStorage.setItem(LS_KEY, pick);
      }
      return data;
    } finally {
      loadedOnce.current = true;
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) load();
    else {
      loadedOnce.current = false;
      setBusinesses([]);
      setLoading(false);
    }
  }, [user, load]);

  const setActive = (id) => {
    setActiveId(id);
    localStorage.setItem(LS_KEY, id);
  };

  const createBusiness = async (name) => {
    const { data } = await api.post("/businesses", { name });
    await load();
    setActive(data.id);
    return data;
  };

  const deleteBusiness = async (id) => {
    await api.delete(`/businesses/${id}`);
    localStorage.removeItem(LS_KEY);
    setActiveId(null);
    await load();
  };

  const active = businesses.find((b) => b.id === activeId) || businesses[0] || null;

  return (
    <BusinessCtx.Provider
      value={{ businesses, active, activeId: active?.id, loading, load, setActive, createBusiness, deleteBusiness }}
    >
      {children}
    </BusinessCtx.Provider>
  );
}

export const useBusiness = () => useContext(BusinessCtx);
